import axios from '@/api/provider';

/**
 * Get current user's roles and permissions
 * @returns {Promise<Object>} { user_id, username, email, roles: [], permissions: [] }
 */
const getMyPermissions = async () => {
  return axios
    .get('rbac/me')
    .then((response) => ({
      userId: response.user_id,
      username: response.username,
      email: response.email,
      roles: response.roles || [],
      permissions: response.permissions || []
    }))
    .catch((error) => {
      console.error('Error fetching user permissions:', error);
      throw error;
    });
};

/**
 * Get all available roles
 * @returns {Promise<Array>} Array of role objects
 */
const getRoles = async () => {
  return axios
    .get('rbac/roles')
    .then((response) => response.roles || response || [])
    .catch((error) => {
      console.error('Error fetching roles:', error);
      throw error;
    });
};

/**
 * Get a single role by ID
 * @param {number} roleId - Role ID to fetch
 * @returns {Promise<Object>} Role object
 */
const getRole = async (roleId) => {
  return axios
    .get(`rbac/roles/${roleId}`)
    .then((response) => response)
    .catch((error) => {
      console.error('Error fetching role:', error);
      throw error;
    });
};

/**
 * Create a new role
 * @param {Object} role - { name, description, permissions: [] }
 * @returns {Promise<Object>} Created role response
 */
const createRole = async (role) => {
  return axios
    .post('rbac/roles', {
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || []
    })
    .then((response) => ({
      success: response.success !== false,
      message: response.message,
      role: response.role || response
    }))
    .catch((error) => {
      console.error('Error creating role:', error);
      throw error;
    });
};

/**
 * Update an existing role
 * @param {number} roleId - Role ID to update
 * @param {Object} updates - { name?, description?, permissions? }
 * @returns {Promise<Object>} Updated role
 */
const updateRole = async (roleId, updates) => {
  return axios
    .put(`rbac/roles/${roleId}`, updates)
    .then((response) => ({
      success: response.success !== false,
      message: response.message,
      role: response.role || response
    }))
    .catch((error) => {
      console.error('Error updating role:', error);
      throw error;
    });
};

/**
 * Delete a role
 * @param {number} roleId - Role ID to delete
 * @returns {Promise<Object>} Delete response
 */
const deleteRole = async (roleId) => {
  return axios
    .del('rbac/roles', roleId)
    .then((response) => ({
      success: response.success !== false,
      message: response.message || 'Role deleted'
    }))
    .catch((error) => {
      console.error('Error deleting role:', error);
      throw error;
    });
};

/**
 * Get all available permissions
 * @returns {Promise<Array>} Array of permission objects
 */
const getPermissions = async () => {
  return axios
    .get('rbac/permissions')
    .then((response) => response.permissions || response || [])
    .catch((error) => {
      console.error('Error fetching permissions:', error);
      throw error;
    });
};

/**
 * Get all users with their roles
 * @returns {Promise<Array>} Array of user objects with roles
 */
const getUsers = async () => {
  return axios
    .get('rbac/users')
    .then((response) => {
      const users = response.users || response || [];
      // Normalize user_id to id for frontend consistency
      return users.map(user => ({
        ...user,
        id: user.id || user.user_id
      }));
    })
    .catch((error) => {
      console.error('Error fetching users:', error);
      throw error;
    });
};

/**
 * Create a new user (admin-initiated). The backend generates a temporary
 * password and emails it to the new user.
 * @param {Object} user - { username, email, roleIds }
 * @returns {Promise<Object>} Created user with roles and permissions
 */
const createUser = async (user) => {
  return axios
    .post('rbac/users', {
      username: user.username,
      email: user.email,
      role_ids: user.roleIds || []
    })
    .then((response) => ({
      ...response,
      id: response.id || response.user_id
    }))
    .catch((error) => {
      console.error('Error creating user:', error);
      throw error;
    });
};

/**
 * Assign roles to a user
 * @param {number} userId - User ID
 * @param {Array<number>} roleIds - Array of role IDs to assign
 * @returns {Promise<Object>} Updated user response
 */
const assignUserRoles = async (userId, roleIds) => {
  return axios
    .put(`rbac/users/${userId}/roles`, { role_ids: roleIds })
    .then((response) => ({
      success: response.success !== false,
      message: response.message,
      user: response.user || response
    }))
    .catch((error) => {
      console.error('Error assigning user roles:', error);
      throw error;
    });
};

export default {
  // Current user permissions
  getMyPermissions,
  // Role management
  getRoles,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  // Permissions list
  getPermissions,
  // User management
  getUsers,
  createUser,
  assignUserRoles
};
